import React, { useEffect, useMemo, useState } from "react";
import { MessageSquare, Send, Check, AlertTriangle, Search, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/**
 * StaffSmsAdminTab — admins pick up to 10 staff users (admins,
 * premium_creators, creators with phones on file) and broadcast a single
 * SMS message to all of them via Twilio.
 *
 * Flow:
 *   1. GET /api/admin/sms/staff       → roster + sms_configured flag
 *   2. Admin picks 1–10 users + types a message (max 480 chars)
 *   3. POST /api/admin/sms/broadcast  → Twilio fan-out, results returned per user
 *   4. GET /api/admin/sms/history     → audit feed below the form
 */
const MAX_PICKED = 10;
const MAX_MESSAGE = 480;

export default function StaffSmsAdminTab() {
  const [smsConfigured, setSmsConfigured] = useState(true);
  const [staff, setStaff] = useState([]);
  const [picked, setPicked] = useState([]);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);

  const refreshStaff = async () => {
    try {
      const { data } = await api.get("/admin/sms/staff");
      setSmsConfigured(!!data?.sms_configured);
      setStaff(data?.staff || []);
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't load staff roster"));
    }
  };

  const refreshHistory = async () => {
    try {
      const { data } = await api.get("/admin/sms/history?limit=20");
      setHistory(data || []);
    } catch { /* non-fatal */ }
  };

  useEffect(() => {
    refreshStaff();
    refreshHistory();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return staff;
    return staff.filter((u) =>
      [u.name, u.email, u.phone, u.role].filter(Boolean).some((s) => s.toLowerCase().includes(q)),
    );
  }, [staff, search]);

  const togglePick = (uid) => {
    setPicked((prev) => {
      if (prev.includes(uid)) return prev.filter((x) => x !== uid);
      if (prev.length >= MAX_PICKED) {
        toast.error(`Up to ${MAX_PICKED} recipients per broadcast`);
        return prev;
      }
      return [...prev, uid];
    });
  };

  /** Quick-fill the picker with everyone matching a role group (admins,
   *  premium_creators, etc.) — capped at MAX_PICKED so we never exceed
   *  the per-broadcast Twilio ceiling. Replaces the current selection
   *  rather than appending so the admin always knows exactly who is on
   *  the list. */
  const pickGroup = (predicate, label) => {
    const matches = staff.filter(predicate).slice(0, MAX_PICKED).map((u) => u.user_id);
    if (matches.length === 0) {
      toast.error(`No staff matches "${label}"`);
      return;
    }
    setPicked(matches);
    toast.success(`Selected ${matches.length} ${label}`);
  };

  const GROUPS = [
    { key: "admins",   label: "All Admins",          predicate: (u) => u.role === "admin" },
    { key: "premium",  label: "All Premium Creators",predicate: (u) => u.role === "premium_creator" },
    { key: "creators", label: "All Creators",        predicate: (u) => u.role === "creator" || u.role === "premium_creator" },
    { key: "everyone", label: "Everyone",            predicate: () => true },
  ];

  const send = async () => {
    if (picked.length === 0) { toast.error("Pick at least one recipient"); return; }
    if (!message.trim()) { toast.error("Type a message"); return; }
    setSending(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/admin/sms/broadcast", {
        user_ids: picked,
        message: message.trim(),
      });
      setLastResult(data);
      const ok = data?.delivered ?? 0;
      const total = data?.target_count ?? picked.length;
      if (ok === total) toast.success(`SMS sent to ${ok}/${total}`);
      else if (ok > 0)  toast.warning(`SMS sent to ${ok}/${total} — see results below`);
      else              toast.error(`Twilio rejected all ${total} sends — see results below`);
      setMessage("");
      setPicked([]);
      await refreshHistory();
    } catch (e) {
      toast.error(formatApiError(e, "Broadcast failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="staff-sms-admin" className="space-y-6">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-black text-2xl text-white tracking-tighter flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-tv-accent" /> Staff SMS Broadcast
          </h2>
          <p className="text-white/55 text-sm mt-1 max-w-xl">
            Send the same SMS to up to {MAX_PICKED} staff members at once. Picks are
            limited to admins, premium creators, and creators who have a phone number on file.
          </p>
        </div>
        <div className={`text-xs uppercase tracking-wider font-bold px-3 py-1.5 rounded-full ${
          smsConfigured ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                        : "bg-red-500/15 text-red-300 border border-red-500/30"
        }`}>
          {smsConfigured ? "Twilio · Connected" : "Twilio · Not configured"}
        </div>
      </header>

      {!smsConfigured && (
        <div data-testid="sms-not-configured" className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-200 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Twilio credentials are missing in <code>backend/.env</code>. Paste your Account SID, Auth Token, and From number, then restart the backend.</span>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Picker */}
        <section className="lg:col-span-2 bg-white/5 rounded-2xl border border-white/10 p-4">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h3 className="font-display font-bold text-white text-sm uppercase tracking-wider">
              Recipients <span className="text-white/40 font-normal normal-case ml-1">({picked.length}/{MAX_PICKED} picked)</span>
            </h3>
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1.5">
              <Search className="w-3.5 h-3.5 text-white/40" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, phone…"
                data-testid="staff-sms-search"
                className="bg-transparent outline-none text-xs text-white placeholder:text-white/30 w-44"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {GROUPS.map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => pickGroup(g.predicate, g.label)}
                data-testid={`staff-quick-group-${g.key}`}
                className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-tv-accent/10 hover:bg-tv-accent/20 text-tv-accent border border-tv-accent/30"
              >
                {g.label}
              </button>
            ))}
            {picked.length > 0 && (
              <button
                type="button"
                onClick={() => setPicked([])}
                data-testid="staff-clear-picked"
                className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-white/5 hover:bg-white/10 text-white/60 border border-white/10"
              >
                Clear
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto pr-1 -mr-1 space-y-1.5">
            {filtered.length === 0 && (
              <div className="text-white/40 text-xs italic py-6 text-center">
                {staff.length === 0
                  ? "No staff users with phone numbers on file. Have them save a phone in their profile."
                  : "No matches — clear search."}
              </div>
            )}
            {filtered.map((u) => {
              const isPicked = picked.includes(u.user_id);
              return (
                <label
                  key={u.user_id}
                  data-testid={`staff-row-${u.user_id}`}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition ${
                    isPicked
                      ? "bg-tv-accent/15 border-tv-accent/40"
                      : "bg-white/5 border-white/10 hover:bg-white/10"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isPicked}
                    onChange={() => togglePick(u.user_id)}
                    data-testid={`staff-toggle-${u.user_id}`}
                    className="w-4 h-4 accent-tv-accent shrink-0"
                  />
                  {u.avatar_url ? (
                    <img src={u.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold text-white/60 shrink-0">
                      {(u.name || u.email || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-white truncate flex items-center gap-1.5">
                      {u.name || u.email || "—"}
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/10 text-white/60 font-bold">{u.role}</span>
                    </div>
                    <div className="text-[11px] text-white/50 truncate">{u.phone}{u.email ? ` · ${u.email}` : ""}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </section>

        {/* Compose + send */}
        <section className="bg-white/5 rounded-2xl border border-white/10 p-4 flex flex-col gap-3">
          <h3 className="font-display font-bold text-white text-sm uppercase tracking-wider">Message</h3>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
            placeholder="Hey team — quick reminder…"
            data-testid="staff-sms-message"
            className="bg-black/40 border border-white/10 rounded-lg p-3 text-sm text-white outline-none focus:border-tv-accent resize-none min-h-[140px]"
            disabled={!smsConfigured}
          />
          <div className="flex items-center justify-between text-[10px] text-white/40 uppercase tracking-wider">
            <span>Max {MAX_MESSAGE} chars</span>
            <span className={message.length > MAX_MESSAGE - 30 ? "text-amber-400" : ""}>{message.length}/{MAX_MESSAGE}</span>
          </div>
          <div className="text-[10px] text-white/40 italic">
            "— Tap In TV Staff" is appended automatically if the message doesn't already mention Tap In TV.
          </div>
          <button
            type="button"
            onClick={send}
            disabled={sending || !smsConfigured || picked.length === 0 || !message.trim()}
            data-testid="staff-sms-send"
            className="bg-tv-accent hover:bg-yellow-400 text-black font-display font-black uppercase tracking-wider py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <Send className="w-4 h-4" />
            {sending ? "Sending…" : `Send to ${picked.length || "0"} ${picked.length === 1 ? "person" : "people"}`}
          </button>
        </section>
      </div>

      {/* Last broadcast results — per-recipient delivery flags */}
      {lastResult && (
        <section data-testid="staff-sms-results" className="bg-white/5 rounded-2xl border border-white/10 p-4">
          <h3 className="font-display font-bold text-white text-sm uppercase tracking-wider mb-3">
            Last broadcast · {lastResult.delivered}/{lastResult.target_count} delivered
          </h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {(lastResult.results || []).map((r) => (
              <div key={r.user_id} className={`flex items-center gap-2 px-3 py-2 rounded border text-xs ${
                r.ok ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200"
                     : "bg-red-500/10 border-red-500/30 text-red-200"
              }`}>
                {r.ok ? <Check className="w-3.5 h-3.5 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="font-bold truncate">{r.name || r.user_id}</div>
                  <div className="text-[10px] opacity-75 truncate">
                    {r.ok ? r.sid : (r.error || "Failed")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* History */}
      {history.length > 0 && (
        <section className="bg-white/5 rounded-2xl border border-white/10 p-4">
          <h3 className="font-display font-bold text-white text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-tv-accent" /> Recent broadcasts
          </h3>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {history.map((h) => (
              <div key={h.id} data-testid={`staff-sms-history-${h.id}`} className="bg-black/30 border border-white/10 rounded-lg p-3 text-xs">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-bold text-white">{h.sent_by_name || h.sent_by}</span>
                  <span className="text-white/45">
                    {h.delivered}/{h.target_count} delivered · {new Date(h.sent_at).toLocaleString()}
                  </span>
                </div>
                <div className="text-white/70 whitespace-pre-line line-clamp-3">{h.message}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
